import { AxiosRequestConfig } from 'axios';
import { axiosClient } from '@as-react/common-utils';
import { createUrl } from '../../utils/createUrl';
import { authMutationsKeys } from './auth.mutations';
import { QueryApiOptions, UrlMapping } from '../../types/types';
import { AuthUserinfoResponse, CheckoutLoginResponse } from '@as-react/common-types';
import { authQueryKeys } from './auth.queries';

const API_BACKEND_BASIC_AUTH = 'Basic Y3VzdG9tZXItYXBwOmN1c3RvbWVyLXNlY3JldA==';

export type AuthLoginGuestParams = {
  username: string;
  exploreMoreCardNumber?: string;
};

export type AuthLoginCheckoutParams = {
  username: string;
  password: string;
  keepSignedIn: boolean;
};

export type OAuthTokenParams = {
  clientId: string;
  code: string;
  codeVerifier: string;
  redirectUri: string;
};

export type OAuthLogoutParams = {
  clientId: string;
  redirectUri: string;
  oidcToken: string;
};

const urlMapping: UrlMapping<typeof authMutationsKeys & typeof authQueryKeys> = {
  loginGuest: {
    client: 'api/buy/login',
  },
  loginCheckout: {
    client: 'api/oauth/login/checkout',
  },
  getUserinfo: {
    client: 'authorization-server/userinfo',
  },
  logout: {
    client: 'authorization-server/connect/logout',
  },
};

export const loginGuest = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  { username, exploreMoreCardNumber }: AuthLoginGuestParams
) => {
  const url = createUrl(servicesEndpoint, urlMapping.loginGuest, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    withCredentials: true,
    headers: {
      Authorization: API_BACKEND_BASIC_AUTH,
    },
    params: defaultRequestParams,
    data: { username, exploreMoreCardNumber },
  };

  const result = await axiosClient.request<undefined>(axiosConfig);
  return result.data;
};

export const loginCheckout = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  { username, password, keepSignedIn }: AuthLoginCheckoutParams
) => {
  const url = createUrl(servicesEndpoint, urlMapping.loginCheckout, isServer);
  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    withCredentials: true,
    headers: {
      Authorization: API_BACKEND_BASIC_AUTH,
    },
    params: {
      ...defaultRequestParams,
      keepSignedIn,
    },
    data: {
      username,
      password,
    },
  };

  const result = await axiosClient.request<CheckoutLoginResponse>(axiosConfig);
  return result.data;
};

export const getUserinfo = async ({ servicesEndpoint }: QueryApiOptions) => {
  const url = createUrl(servicesEndpoint, urlMapping.getUserinfo);
  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    withCredentials: true,
  };

  const result = await axiosClient.request<AuthUserinfoResponse>(axiosConfig);
  return result.data;
};

export const logout = async (
  { servicesEndpoint }: QueryApiOptions,
  { clientId, redirectUri, oidcToken }: OAuthLogoutParams
) => {
  const url = createUrl(servicesEndpoint, urlMapping.logout);
  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    withCredentials: true,
    params: {
      client_id: clientId,
      post_logout_redirect_uri: redirectUri,
      id_token_hint: oidcToken,
    },
  };

  const result = await axiosClient.request<unknown>(axiosConfig);
  return result.data;
};
